import type { GroupSummary } from '../utils/computeGroupSummary';
import type { SessionFullTractMetrics } from '../utils/computeSessionFullTractMetrics';
import type {
  CustomTimeSegment,
  SpectralWorkspaceSession,
  TrendSignalKey,
} from '../types';
import type { ExcelExportSheet } from './buildExcelWorkbook';
import type { OverviewExportMeta } from './buildOverviewExport';
import { buildSessionsExport } from './buildSessionsExport';
import {
  buildSpectraExportSheets,
  buildSpectraExportSheetName,
} from './buildSpectraExport';
import {
  buildStatisticsValidationExportSheets,
  buildStatisticsValidationSheetName,
} from './buildStatisticsValidationExport';
import { buildTrendExport, type TrendExportContext } from './buildTrendExport';
import {
  resolveTrendExportViewIds,
  resolveViewSheetLabel,
} from './computeTrendExportSeries';
import { buildExcelExportFileName } from './formatExportFilename';
import {
  buildSessionsForRawHrDataTrend,
  WORKSPACE_HRDATA_RAW_EXPORT,
} from './buildHrDataRawExport';
import {
  buildSessionDatesExportSheetNames,
  buildSessionDatesExportSheets,
} from './buildSessionDatesExport';

export { WORKSPACE_HRDATA_RAW_EXPORT } from './buildHrDataRawExport';

export const WORKSPACE_TREND_EXPORT_FEATURES: TrendSignalKey[] = [
  'HR',
  'HRV',
  'LF',
  'HF',
  'RR',
];

export const WORKSPACE_TREND_EXPORT_VARIANTS = [
  {
    id: 'reale',
    label: 'Senza filtri',
    fileSuffix: 'reale',
    trendNormalizationEnabled: false,
    trendLowPassEnabled: false,
  },
  {
    id: 'passa',
    label: 'Passabasso',
    fileSuffix: 'passa',
    trendNormalizationEnabled: false,
    trendLowPassEnabled: true,
  },
  {
    id: 'norm',
    label: 'Normalizzato',
    fileSuffix: 'norm',
    trendNormalizationEnabled: true,
    trendLowPassEnabled: false,
  },
  {
    id: 'norm-passa',
    label: 'Normalizzato + passabasso',
    fileSuffix: 'norm-passa',
    trendNormalizationEnabled: true,
    trendLowPassEnabled: true,
  },
] as const;

export const WORKSPACE_SUMMARY_EXPORT = {
  id: 'summary',
  label: 'Lista sessioni e Validazione statistica',
  section: 'sessioni-validazione-statistica',
} as const;

export const WORKSPACE_SPECTRA_EXPORT = {
  id: 'spectra',
  label: 'Spettri',
  section: 'spettri',
} as const;

export type WorkspaceTrendExportVariant =
  (typeof WORKSPACE_TREND_EXPORT_VARIANTS)[number];

export type WorkspaceTrendExportVariantId = WorkspaceTrendExportVariant['id'];

export type WorkspaceSummaryExportId = typeof WORKSPACE_SUMMARY_EXPORT.id;

export type WorkspaceSpectraExportId = typeof WORKSPACE_SPECTRA_EXPORT.id;

export type WorkspaceHrDataRawExportId = typeof WORKSPACE_HRDATA_RAW_EXPORT.id;

export type WorkspaceExportFileId =
  | WorkspaceTrendExportVariantId
  | WorkspaceSummaryExportId
  | WorkspaceSpectraExportId
  | WorkspaceHrDataRawExportId;

export const buildTrendExportSheetName = (
  feature: TrendSignalKey,
  viewSheetLabel: string
) => `Evoluzione ${feature} - ${viewSheetLabel}`;

export type BuildWorkspaceExcelExportParams = {
  meta: OverviewExportMeta;
  summary: GroupSummary;
  exportDateLabel: string;
  exportDate: Date;
  sessions: SpectralWorkspaceSession[];
  metrics: SessionFullTractMetrics[];
  readySessions: SpectralWorkspaceSession[];
  customSegments: CustomTimeSegment[];
  trendStart: string | null;
  trendEnd: string | null;
  showOverlaySessions: boolean;
  selectedOverlaySessionIds: string[];
  selectedSpectraSessionId: string | null;
  spectraStart: string | null;
  spectraEnd: string | null;
  spectraLogScale: boolean;
};

export const countWorkspaceTrendSheets = (
  customSegments: CustomTimeSegment[]
) =>
  WORKSPACE_TREND_EXPORT_FEATURES.length *
    resolveTrendExportViewIds(customSegments).length +
  buildSessionDatesExportSheetNames().length;

export const buildWorkspaceTrendSheetNames = (
  customSegments: CustomTimeSegment[]
) => {
  const viewIds = resolveTrendExportViewIds(customSegments);

  return [
    ...WORKSPACE_TREND_EXPORT_FEATURES.flatMap(feature =>
      viewIds.map(viewId =>
        buildTrendExportSheetName(
          feature,
          resolveViewSheetLabel(viewId, customSegments)
        )
      )
    ),
    ...buildSessionDatesExportSheetNames(),
  ];
};

export const buildWorkspaceSummarySheetNames = (
  customSegments: CustomTimeSegment[]
) => [
  'Lista sessioni',
  ...resolveTrendExportViewIds(customSegments).map(viewId =>
    buildStatisticsValidationSheetName(
      resolveViewSheetLabel(viewId, customSegments)
    )
  ),
  ...buildSessionDatesExportSheetNames(),
];

export const buildWorkspaceSpectraSheetNames = (
  customSegments: CustomTimeSegment[]
) => [
  ...resolveTrendExportViewIds(customSegments).map(viewId =>
    buildSpectraExportSheetName(resolveViewSheetLabel(viewId, customSegments))
  ),
  ...buildSessionDatesExportSheetNames(),
];

export const buildHrDataRawExportSheetNames = (
  customSegments: CustomTimeSegment[] = []
) => buildWorkspaceTrendSheetNames(customSegments);

export type WorkspaceExportPreview = {
  id: WorkspaceExportFileId;
  label: string;
  filename: string;
  sheetNames: string[];
  sheetCount: number;
};

export const buildWorkspaceExportPreviews = ({
  meta,
  exportDate,
  customSegments,
}: {
  meta: OverviewExportMeta;
  exportDate: Date;
  customSegments: CustomTimeSegment[];
}): WorkspaceExportPreview[] => {
  const trendSheetNames = buildWorkspaceTrendSheetNames(customSegments);
  const summarySheetNames = buildWorkspaceSummarySheetNames(customSegments);
  const spectraSheetNames = buildWorkspaceSpectraSheetNames(customSegments);
  const hrDataRawSheetNames = buildHrDataRawExportSheetNames(customSegments);

  const trendExports = WORKSPACE_TREND_EXPORT_VARIANTS.map(variant => ({
    id: variant.id,
    label: variant.label,
    filename: buildExcelExportFileName({
      analysisName: meta.name,
      section: 'evoluzione-segnali',
      variantSuffix: variant.fileSuffix,
      exportDate,
    }),
    sheetNames: trendSheetNames,
    sheetCount: trendSheetNames.length,
  }));

  return [
    ...trendExports,
    {
      id: WORKSPACE_SUMMARY_EXPORT.id,
      label: WORKSPACE_SUMMARY_EXPORT.label,
      filename: buildExcelExportFileName({
        analysisName: meta.name,
        section: WORKSPACE_SUMMARY_EXPORT.section,
        exportDate,
      }),
      sheetNames: summarySheetNames,
      sheetCount: summarySheetNames.length,
    },
    {
      id: WORKSPACE_SPECTRA_EXPORT.id,
      label: WORKSPACE_SPECTRA_EXPORT.label,
      filename: buildExcelExportFileName({
        analysisName: meta.name,
        section: WORKSPACE_SPECTRA_EXPORT.section,
        exportDate,
      }),
      sheetNames: spectraSheetNames,
      sheetCount: spectraSheetNames.length,
    },
    {
      id: WORKSPACE_HRDATA_RAW_EXPORT.id,
      label: WORKSPACE_HRDATA_RAW_EXPORT.label,
      filename: buildExcelExportFileName({
        analysisName: meta.name,
        section: WORKSPACE_HRDATA_RAW_EXPORT.section,
        exportDate,
      }),
      sheetNames: hrDataRawSheetNames,
      sheetCount: hrDataRawSheetNames.length,
    },
  ];
};

export const buildWorkspaceTrendExcelSheetsForVariant = (
  variant: WorkspaceTrendExportVariant,
  {
    readySessions,
    customSegments,
    trendStart,
    trendEnd,
    showOverlaySessions,
    selectedOverlaySessionIds,
  }: Pick<
    BuildWorkspaceExcelExportParams,
    | 'readySessions'
    | 'customSegments'
    | 'trendStart'
    | 'trendEnd'
    | 'showOverlaySessions'
    | 'selectedOverlaySessionIds'
  >
): ExcelExportSheet[] => {
  const context: TrendExportContext = {
    readySessions,
    customSegments,
    trendStart,
    trendEnd,
    trendNormalizationEnabled: variant.trendNormalizationEnabled,
    trendLowPassEnabled: variant.trendLowPassEnabled,
    showOverlaySessions,
    selectedOverlaySessionIds,
  };

  const viewIds = resolveTrendExportViewIds(customSegments);

  return [
    ...WORKSPACE_TREND_EXPORT_FEATURES.flatMap(feature =>
      viewIds.map(viewId => ({
        name: buildTrendExportSheetName(
          feature,
          resolveViewSheetLabel(viewId, customSegments)
        ),
        rows: buildTrendExport(context, feature, viewId),
      }))
    ),
    ...buildSessionDatesExportSheets(readySessions),
  ];
};

export const buildWorkspaceSummaryExcelSheets = ({
  meta,
  sessions,
  metrics,
  readySessions,
  customSegments,
  trendStart,
  trendEnd,
}: Pick<
  BuildWorkspaceExcelExportParams,
  | 'meta'
  | 'sessions'
  | 'metrics'
  | 'readySessions'
  | 'customSegments'
  | 'trendStart'
  | 'trendEnd'
>): ExcelExportSheet[] => [
  {
    name: 'Lista sessioni',
    rows: buildSessionsExport({
      analysisName: meta.name,
      sessions,
      metrics,
      exportDateLabel: '',
    }),
  },
  ...buildStatisticsValidationExportSheets({
    readySessions,
    customSegments,
    trendStart,
    trendEnd,
  }),
  ...buildSessionDatesExportSheets(sessions),
];

export const buildWorkspaceSpectraExcelSheets = ({
  readySessions,
  customSegments,
  trendStart,
  trendEnd,
  selectedSpectraSessionId,
  spectraStart,
  spectraEnd,
  spectraLogScale,
}: Pick<
  BuildWorkspaceExcelExportParams,
  | 'readySessions'
  | 'customSegments'
  | 'trendStart'
  | 'trendEnd'
  | 'selectedSpectraSessionId'
  | 'spectraStart'
  | 'spectraEnd'
  | 'spectraLogScale'
>): ExcelExportSheet[] => [
  ...buildSpectraExportSheets({
    readySessions,
    customSegments,
    trendStart,
    trendEnd,
    selectedSessionId: selectedSpectraSessionId,
    spectraStart,
    spectraEnd,
    logScale: spectraLogScale,
  }),
  ...buildSessionDatesExportSheets(readySessions),
];
export type WorkspaceExcelExportFile = {
  id: WorkspaceExportFileId;
  label: string;
  filename: string;
  sheets: ExcelExportSheet[];
};

export const buildWorkspaceExcelExportFile = (
  exportId: WorkspaceExportFileId,
  params: BuildWorkspaceExcelExportParams
): WorkspaceExcelExportFile => {
  const trendVariant = WORKSPACE_TREND_EXPORT_VARIANTS.find(
    variant => variant.id === exportId
  );

  if (trendVariant) {
    return {
      id: exportId,
      label: trendVariant.label,
      filename: buildExcelExportFileName({
        analysisName: params.meta.name,
        section: 'evoluzione-segnali',
        variantSuffix: trendVariant.fileSuffix,
        exportDate: params.exportDate,
      }),
      sheets: buildWorkspaceTrendExcelSheetsForVariant(trendVariant, params),
    };
  }

  if (exportId === WORKSPACE_SUMMARY_EXPORT.id) {
    return {
      id: exportId,
      label: WORKSPACE_SUMMARY_EXPORT.label,
      filename: buildExcelExportFileName({
        analysisName: params.meta.name,
        section: WORKSPACE_SUMMARY_EXPORT.section,
        exportDate: params.exportDate,
      }),
      sheets: buildWorkspaceSummaryExcelSheets(params),
    };
  }

  if (exportId === WORKSPACE_SPECTRA_EXPORT.id) {
    return {
      id: exportId,
      label: WORKSPACE_SPECTRA_EXPORT.label,
      filename: buildExcelExportFileName({
        analysisName: params.meta.name,
        section: WORKSPACE_SPECTRA_EXPORT.section,
        exportDate: params.exportDate,
      }),
      sheets: buildWorkspaceSpectraExcelSheets(params),
    };
  }

  if (exportId === WORKSPACE_HRDATA_RAW_EXPORT.id) {
    const realeVariant = WORKSPACE_TREND_EXPORT_VARIANTS.find(
      variant => variant.id === 'reale'
    )!;

    return {
      id: exportId,
      label: WORKSPACE_HRDATA_RAW_EXPORT.label,
      filename: buildExcelExportFileName({
        analysisName: params.meta.name,
        section: WORKSPACE_HRDATA_RAW_EXPORT.section,
        exportDate: params.exportDate,
      }),
      sheets: buildWorkspaceTrendExcelSheetsForVariant(realeVariant, {
        readySessions: buildSessionsForRawHrDataTrend(params.readySessions),
        customSegments: params.customSegments,
        trendStart: params.trendStart,
        trendEnd: params.trendEnd,
        showOverlaySessions: true,
        selectedOverlaySessionIds: [],
      }),
    };
  }

  throw new Error(`Export file non supportato: ${exportId}`);
};

export const buildWorkspaceExcelExports = (
  params: BuildWorkspaceExcelExportParams
): WorkspaceExcelExportFile[] =>
  buildWorkspaceExportPreviews({
    meta: params.meta,
    exportDate: params.exportDate,
    customSegments: params.customSegments,
  }).map(preview => buildWorkspaceExcelExportFile(preview.id, params));

/** @deprecated Use buildWorkspaceTrendExcelSheetsForVariant */
export const buildWorkspaceExcelSheetsForVariant = (
  variant: WorkspaceTrendExportVariant,
  params: Omit<BuildWorkspaceExcelExportParams, 'exportDate'>
) => buildWorkspaceTrendExcelSheetsForVariant(variant, params);

/** @deprecated Use buildWorkspaceExcelExports */
export const buildWorkspaceExcelSheets = (
  params: BuildWorkspaceExcelExportParams
) =>
  buildWorkspaceTrendExcelSheetsForVariant(
    WORKSPACE_TREND_EXPORT_VARIANTS[0],
    params
  );

export const WORKSPACE_EXCEL_SHEET_COUNT = countWorkspaceTrendSheets([]);
